import { delay } from 'redux-saga';
import { takeEvery, call, put, all, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import {
  defineKqlQuery,
  fetchVersion,
  fetchProfile,
  fetchSpace,
  fetchKapps,
  fetchForms,
  fetchDefaultLocale,
  fetchLocales,
  fetchTimezones,
  fetchTeams,
  fetchUser,
  fetchUsers,
  updateProfile,
  importLocale,
  fetchCategories,
} from '@kineticdata/react';
import semver from 'semver';
import {
  addToast,
  addToastAlert,
  enableSearchHistory,
  selectServicesKappSlug,
  C,
  Utils,
} from '@kineticdata/bundle-common';

import { actions, types } from '../modules/app';
import { actions as alertsActions } from '../modules/alerts';
import { SPACE_ATTR_AGENCY_CODES } from '@kineticdata/bundle-components/src/constants';

const MINIMUM_CE_VERSION = '5.2.0';
const SPACE_INCLUDES = [
  'details',
  'attributes',
  'attributesMap',
  'authorization',
  'userProfileAttributeDefinitions',
];
const KAPP_INCLUDES = [
  'details',
  'attributes',
  'attributesMap',
  'authorization',
];
const FORM_INCLUDES = [
  'attributesMap[Icon]',
  'attributesMap[Image]',
  'attributesMap[Funding Dependent]',
  'attributesMap[Approvals]',
  'attributesMap[Internal]',
  'categorizations.category',
  'kapp.attributesMap[Default Form Image]',
];
const CATEGORY_INCLUDES = [
  'attributesMap',
  'categorizations.form',
  'categorizations.form.attributes[Icon]',
  'categorizations.form.attributes[Image],',
  'categorizations.form.attributes[Funding Dependent],',
  'categorizations.form.attributes[Internal],',
  'categorizations.form.kapp.attributesMap[Default Form Image],',
];
const PROFILE_INCLUDES = [
  'details',
  'attributes',
  'profileAttributes',
  'profileAttributesMap',
  'memberships',
  'memberships.team',
  'memberships.team.attributes',
  'memberships.team.attributesMap',
];

// Fetch Entire App
export function* fetchAppTask({ payload }) {
  const authenticated = yield select(state => state.app.authenticated);
  const { version } = yield call(fetchVersion, { public: !authenticated });
  // Check to make sure the version is compatible with this bundle.
  if (
    semver.satisfies(semver.coerce(version.version), `>=${MINIMUM_CE_VERSION}`)
  ) {
    // Set data needed for initialization from server into redux
    yield all([
      call(fetchSpaceTask, authenticated),
      call(fetchKappsTask, authenticated),
      call(fetchProfileTask, authenticated),
      call(fetchLocaleMetaTask, authenticated),
      put(actions.setCoreVersion(version.version)),
    ]);

    // Fetch data needed for initialization from redux
    const { profile, kapps, errors } = yield select(state => ({
      profile: state.app.profile,
      kapps: state.app.kapps,
      errors: state.app.errors,
    }));

    // Make sure there were no errors fetching metadata
    if (errors.isEmpty()) {
      // Fetch additional data and configure Search History for Kapps
      yield all([
        call(fetchFormsTask, authenticated),
        call(fetchCategoriesTask, authenticated),
        call(fetchPortalPagesTask, authenticated),
        ...kapps.map(kapp => {
          return put(
            enableSearchHistory({
              kappSlug: kapp.slug,
              value: Utils.getAttributeValue(kapp, 'Record Search History', ''),
            }),
          );
        }),
      ]);

      // Preload locale before displaying the app to get rid of flicker
      if (profile.preferredLocale) {
        importLocale(profile.preferredLocale);
        yield put(actions.setUserLocale(profile.preferredLocale));
      } else {
        const { defaultLocale } = yield call(fetchDefaultLocale, {
          public: !authenticated,
        });
        importLocale((defaultLocale && defaultLocale.code) || 'en');
        yield put(actions.setUserLocale(defaultLocale && defaultLocale.code));
      }

      yield put(actions.fetchAppSuccess());
      if (authenticated) {
        yield put(alertsActions.fetchAlertsRequest());
      }
    } else {
      yield put(actions.fetchAppFailure());
    }
  } else {
    window.alert(
      `You must be running Kinetic Request v${MINIMUM_CE_VERSION} or later in order to use this app. You are currently running v${
        version.version
      }.`,
    );
  }
}

// Fetch Kapps Task with forms for services kapp
export function* fetchKappsTask(authenticated) {
  const { kapps, error } = yield call(fetchKapps, {
    include: KAPP_INCLUDES.join(','),
    limit: 1000,
    public: !authenticated,
  });
  if (error) {
    yield put(actions.fetchKappsFailure(error));
  } else {
    yield put(actions.fetchKappsSuccess(kapps));
  }
}

// Fetch Space Task
export function* fetchSpaceTask(authenticated) {
  const { space, error } = yield call(fetchSpace, {
    include: SPACE_INCLUDES.join(','),
    public: !authenticated,
  });
  if (error) {
    yield put(actions.fetchSpaceFailure(error));
  } else {
    yield put(actions.fetchSpaceSuccess(space));
  }
}

// Fetch Portal Pages Task
export function* fetchPortalPagesTask(authenticated) {
  const servicesKappSlug = yield select(selectServicesKappSlug);
  if (servicesKappSlug) {
    const { forms } = yield call(fetchForms, {
      kappSlug: servicesKappSlug,
      include: `attributesMap,kapp,kapp.attributes[${
        C.ATTRIBUTE_KAPP_DEFAULT_FORM_IMAGE
      }]`,
      limit: 1000,
      public: !authenticated,
      q: 'type = "Portal Page"',
    });
    yield put(
      actions.fetchPortalPagesComplete((forms || []).map(addPortalPageMeta)),
    );
  }
}

// Fetch Forms Task
export function* fetchFormsTask(authenticated) {
  const servicesKappSlug = yield select(selectServicesKappSlug);
  if (servicesKappSlug) {
    // Define temp variables for fetching all forms for the services kapp
    let formList = [],
      done = false,
      pageToken = null;
    // While there are forms to fetch, fetch them
    while (!done) {
      const { forms, error: formsError, nextPageToken } = yield call(
        fetchForms,
        {
          kappSlug: servicesKappSlug,
          include: FORM_INCLUDES.join(','),
          limit: 1000,
          q: defineKqlQuery()
            .in('status', 'statuses')
            .end()({
            statuses: ['New', 'Active'],
          }),
          pageToken,
          public: !authenticated,
        },
      );

      if (formsError) {
        // If error, set empty list
        formList = [];
        done = true;
      } else {
        formList = [...formList, ...forms];
        pageToken = nextPageToken;
        done = !nextPageToken;
      }
    }

    yield put(
      actions.fetchFormsComplete([{ slug: servicesKappSlug, forms: formList }]),
    );
  }
}

// Fetch Categories Task
export function* fetchCategoriesTask(authenticated) {
  const servicesKappSlug = yield select(selectServicesKappSlug);
  if (servicesKappSlug) {
    const { categories } = yield call(fetchCategories, {
      kappSlug: servicesKappSlug,
      include: CATEGORY_INCLUDES.join(','),
      limit: 1000,
      public: !authenticated,
    });
    yield put(
      actions.fetchCategoriesComplete([
        {
          slug: servicesKappSlug,
          categories: categories
            ? categories.filter(
                // Filter out Service Catalog categories - these are deprecated and will be removed
                c => (c.attributesMap['Service Category'] || []).length === 0,
              )
            : [],
        },
      ]),
    );
  }
}

// Helper function for adding meta data to portal pages
const addPortalPageMeta = form => ({
  ...form,
  meta: {
    isMenuLink: Utils.hasAttributeValue(
      form,
      C.FORM_ATTR_IS_MENU_LINK,
      ['true', 'yes'],
      true,
    ),
    groupName: Utils.getAttributeValue(form, C.FORM_ATTR_PAGE_GROUP_NAME, ''),
    sortOrder:
      parseInt(
        Utils.getAttributeValue(form, C.FORM_ATTR_PAGE_SORT_ORDER, '10000'),
        10,
      ) || 10000,
    icon: Utils.getIcon(form, 'arrow-circle-right'),
  },
});

// Fetch Profile Task called via action. Triggers fetchProfileTask
export function* fetchOnlyProfileTask() {
  const authenticated = yield select(state => state.app.authenticated);

  yield call(fetchProfileTask, authenticated);
}

// Fetch Profile Task
export function* fetchProfileTask(authenticated) {
  const { profile, error } = yield call(fetchProfile, {
    include: PROFILE_INCLUDES.join(','),
    public: !authenticated,
  });
  if (error) {
    yield put(actions.fetchProfileFailure(error));
  } else {
    // Wait until space is fetched before setting profile data since we need
    // space info to map the profile object. Max wait of 1 minute.
    let space = yield select(state => state.app.space),
      tryCounter = 120;
    while (!space && tryCounter > 0) {
      tryCounter--;
      yield delay(500);
      space = yield select(state => state.app.space);
    }
    if (!space) {
      yield put(
        actions.fetchProfileFailure({
          message: 'Fetching of profile record timed out.',
        }),
      );
    } else {
      yield put(actions.fetchProfileSuccess(mapProfile(profile, space)));
    }
  }
}

// Update Profile Task
export function* updateProfileTask({ payload }) {
  const preferredLocale = yield select(
    state => state.app.profile.preferredLocale,
  );

  const { profile, error } = yield call(updateProfile, {
    include: PROFILE_INCLUDES.join(','),
    profile: payload,
  });

  if (error) {
    addToastAlert({
      title: 'Failed to update profile',
      message: error.message,
    });
    yield put(actions.updateProfileComplete());
  } else {
    addToast('Profile updated successfully');
    const space = yield select(state => state.app.space);
    yield put(actions.updateProfileComplete(mapProfile(profile, space)));

    if (profile.preferredLocale !== preferredLocale) {
      yield put(actions.fetchApp());
    }
  }
}

// Toggle Favorite Task
export function* toggleFavoriteTask({ payload }) {
  if (payload) {
    const me = yield select(state => state.app.profile);
    const favoritesCache = yield select(state => state.app.favoritesCache);
    const favoritesCacheTimestamp = yield select(
      state => state.app.favoritesCacheTimestamp,
    );
    const favoritesList =
      Array.isArray(favoritesCache) &&
      favoritesCacheTimestamp &&
      new Date().getTime() - favoritesCacheTimestamp < 120000
        ? favoritesCache
        : me.profileAttributesMap[C.ATTRIBUTE_USER_PROFILE_FAVORITES];

    if (Array.isArray(favoritesList)) {
      const newFavorites = favoritesList.includes(payload)
        ? favoritesList.filter(sf => sf !== payload)
        : favoritesList.concat(payload);
      yield put(actions.updateFavoritesCache(newFavorites));

      const { error } = yield call(updateProfile, {
        profile: {
          profileAttributesMap: {
            [C.ATTRIBUTE_USER_PROFILE_FAVORITES]: newFavorites,
          },
        },
      });

      if (error) {
        console.log(error);
        addToast({ severity: 'danger', message: 'Failed to update favorite' });
      } else {
        addToast('Favorite successfully updated');
        yield put(actions.fetchProfileRequest());
      }
    }
  }
}

// Fetch Locales Metadata Task
export function* fetchLocaleMetaTask() {
  const { locales, timezones } = yield all({
    locales: call(fetchLocales),
    timezones: call(fetchTimezones),
  });
  yield put(
    actions.fetchLocaleMetaSuccess({
      locales: locales.data.locales,
      timezones: timezones.data.timezones,
    }),
  );
}

export function* setAuthenticatedTask() {
  const { authenticated, authRedirect } = yield select(state => state.app);
  if (authenticated && authRedirect) {
    yield put(push(authRedirect));
  }
}

export function* fetchSubteamsSaga({ payload = {} }) {
  const subteamsMap = yield select(state => state.app.subteamsMap);

  // Only fetch the subteams if they haven't been fetched already or if strict flag is true
  if (
    payload.teamName &&
    (!!payload.strict || !subteamsMap.has(payload.teamName))
  ) {
    const { teams } = yield call(fetchTeams, {
      q: `name =* "${payload.teamName}::"`,
      limit: 1000,
      include: [
        'attributes',
        'memberships.user',
        ...C.MAIN_USER_ATTRIBUTES.map(
          attr => `memberships.user.attributes[${attr}]`,
        ),
      ].join(','),
    });

    yield put(
      actions.fetchSubteamsComplete({
        teamName: payload.teamName,
        subteams: teams || [],
      }),
    );
  }
}

export function* fetchUsersSaga({ payload = {} }) {
  const usersMap = yield select(state => state.app.usersMap);
  const usernames = (Array.isArray(payload.usernames)
    ? payload.usernames
    : [payload.usernames]
  ).filter(u => !!payload.strict || !usersMap.get(u));

  // Only fetch the subteams if they haven't been fetched already or if strict flag is true
  if (payload.usernames && usernames.length > 0) {
    const { users, user } = yield call(
      usernames.length > 1 ? fetchUsers : fetchUser,
      usernames.length > 1
        ? {
            q: `username IN (${usernames.map(u => `"${u}"`).join(',')})`,
            limit: 1000,
            include: C.MAIN_USER_ATTRIBUTES.map(
              attr => `attributes[${attr}]`,
            ).join(','),
          }
        : {
            username: usernames[0],
            include: C.MAIN_USER_ATTRIBUTES.map(
              attr => `attributes[${attr}]`,
            ).join(','),
          },
    );

    yield put(
      actions.fetchUsersComplete({
        users: users || (user && [user]) || [],
        usernames,
      }),
    );
  }
}

export function* watchApp() {
  yield takeEvery(types.FETCH_APP_REQUEST, fetchAppTask);
  yield takeEvery(types.FETCH_PROFILE_REQUEST, fetchOnlyProfileTask);
  yield takeEvery(types.UPDATE_PROFILE_REQUEST, updateProfileTask);
  yield takeEvery(types.TOGGLE_FAVORITE_REQUEST, toggleFavoriteTask);
  yield takeEvery(types.SET_AUTHENTICATED, setAuthenticatedTask);
  yield takeEvery(types.FETCH_SUBTEAMS_REQUEST, fetchSubteamsSaga);
  yield takeEvery(types.FETCH_USERS_REQUEST, fetchUsersSaga);
}

const mapProfile = (profile, space) => {
  if (!profile) {
    return profile;
  }
  const agreements = getUserAgreements(profile);
  const agencies = getUserAgencies(profile, space);
  const isSMO = Utils.isMemberOf(profile, C.SMO_TEAM_NAME),
    isDISC = Utils.getAttributeValues(space, 'DISC Companies', []).includes(
      Utils.getAttributeValue(profile, C.ATTRIBUTE_USER_COMPANY),
    ),
    isCatalogManager = Utils.isMemberOfDescendant(
      profile,
      C.CATALOG_TECHNICAL_OWNERS_TEAM_NAME,
    );
  return {
    ...profile,
    validEauthAssuranceLevel:
      Utils.hasAttributeValue(
        profile,
        C.ATTRIBUTE_USER_EAUTH_ASSURANCE_LEVEL,
        '2',
      ) ||
      Utils.getAttributeValues(
        space,
        C.SPACE_USER_EAUTH_ASSURANCE_WHITE_LIST,
        [],
      ).includes(profile.username),
    agreements,
    hasAgreements: agreements.length > 0,
    agencies,
    superUser: profile.spaceAdmin || agreements.includes(C.SUPER_USER_ROLE),
    superUserRO: agreements.includes(C.SUPER_USER_RO_ROLE),
    isSMO,
    isDISC,
    isCatalogManager,
    hasInternalCatalogAccess: isSMO || isDISC || isCatalogManager,
  };
};

export const getUserAgreements = user =>
  [
    ...Utils.getAttributeValue(user, C.ATTRIBUTE_USER_AGREEMENTS, '').split(
      '^',
    ),
    ...Utils.getAttributeValue(
      user,
      C.ATTRIBUTE_USER_AGREEMENTS_EXTRA,
      '',
    ).split('^'),
  ]
    .map(agreement => {
      const match = agreement.match(/^OCIO_NITCPORTAL_(.*)$/i);
      return match && match[1];
    })
    .filter(a => a);

export const hasUserAgreement = (user, agreement) =>
  !!getUserAgreements(user).find(a => a === agreement);

export const getUserAgencies = (user, space) => {
  try {
    const agencyCodes = JSON.parse(
      Utils.getAttributeValue(space, C.SPACE_ATTR_AGENCY_CODES, '{}'),
    );
    return [
      ...Utils.getAttributeValue(
        user,
        C.ATTRIBUTE_USER_EAUTH_AGENCIES,
        '',
      ).split('^'),
      ...Utils.getAttributeValue(
        user,
        C.ATTRIBUTE_USER_EAUTH_AGENCIES_EXTRA,
        '',
      ).split('^'),
    ]
      .filter(agency => agency.split(':')[1] === 'A')
      .map(agency =>
        agencyCodes[agency.split(':')[0].split('_')[1]]?.toUpperCase(),
      )
      .filter(Boolean);
  } catch (e) {
    console.error('Failed to parse Agency Codes space attribute.');
    return [];
  }
};

export const hasUserAgency = (user, space, agency) =>
  !!getUserAgencies(user, space).find(a => a === agency);
